import React, { useState, useEffect, useCallback } from 'react';
import { PostInteractions, QuotedPostEmbed } from './XPlatformFeatures';

const SearchPage = ({ 
  token, 
  onViewProfile,
  onBack,
  onLike,
  onRepost,
  onQuote,
  onComment,
  onShare,
  currentUserId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, users, posts
  const [results, setResults] = useState({ users: [], posts: [], total_users: 0, total_posts: 0 });
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [postsInteractions, setPostsInteractions] = useState({});

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Debounced search
  const performSearch = useCallback(async (query, type) => {
    if (!query || query.length < 2) {
      setResults({ users: [], posts: [], total_users: 0, total_posts: 0 });
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const response = await fetch(
        `${API_URL}/api/search?q=${encodeURIComponent(query)}&type=${type}&limit=30`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data);

        // Fetch interactions for posts
        if (data.posts && data.posts.length > 0) {
          const postIds = data.posts.map(p => p.id);
          const interactionsResponse = await fetch(`${API_URL}/api/world-chat/posts/interactions`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(postIds)
          });
          if (interactionsResponse.ok) {
            const interactionsData = await interactionsResponse.json();
            setPostsInteractions(interactionsData);
          }
        }
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // Search on query change with debounce
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      performSearch(searchQuery, activeTab);
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, activeTab, performSearch]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'acum';
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' });
  };

  // Handle follow toggle
  const handleFollowToggle = async (userId, isCurrentlyFollowing) => {
    try {
      if (isCurrentlyFollowing) {
        await fetch(`${API_URL}/api/users/unfollow/${userId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        await fetch(`${API_URL}/api/users/follow/${userId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      // Update local state
      setResults(prev => ({
        ...prev,
        users: prev.users.map(u => 
          u.id === userId 
            ? { ...u, is_following: !isCurrentlyFollowing, followers_count: isCurrentlyFollowing ? u.followers_count - 1 : u.followers_count + 1 }
            : u
        )
      }));
    } catch (error) {
      console.error('Follow toggle error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Search Header */}
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-md border-b border-gray-800">
        <div className="p-4">
          {/* Back button and Search Input */}
          <div className="flex items-center space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-800 rounded-full transition-colors flex-shrink-0"
                data-testid="search-back-button"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Caută utilizatori sau postări..."
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                data-testid="search-input"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="w-5 h-5 text-gray-500 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 py-3 text-center font-semibold transition-colors ${
              activeTab === 'all'
                ? 'text-white border-b-2 border-cyan-500'
                : 'text-gray-500 hover:bg-gray-800/50'
            }`}
            data-testid="tab-all"
          >
            Toate
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 py-3 text-center font-semibold transition-colors ${
              activeTab === 'users'
                ? 'text-white border-b-2 border-cyan-500'
                : 'text-gray-500 hover:bg-gray-800/50'
            }`}
            data-testid="tab-users"
          >
            Utilizatori {results.total_users > 0 && `(${results.total_users})`}
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`flex-1 py-3 text-center font-semibold transition-colors ${
              activeTab === 'posts'
                ? 'text-white border-b-2 border-cyan-500'
                : 'text-gray-500 hover:bg-gray-800/50'
            }`}
            data-testid="tab-posts"
          >
            Postări {results.total_posts > 0 && `(${results.total_posts})`}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-500"></div>
          </div>
        ) : !hasSearched ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-gray-500">Caută utilizatori sau postări</p>
            <p className="text-gray-600 text-sm mt-1">Introdu cel puțin 2 caractere</p>
          </div>
        ) : (results.users.length === 0 && results.posts.length === 0) ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-500">Niciun rezultat pentru "{searchQuery}"</p>
            <p className="text-gray-600 text-sm mt-1">Încearcă alte cuvinte cheie</p>
          </div>
        ) : (
          <>
            {/* Users Results */}
            {(activeTab === 'all' || activeTab === 'users') && results.users.length > 0 && (
              <div>
                {activeTab === 'all' && (
                  <div className="px-4 py-3 border-b border-gray-800">
                    <h3 className="text-lg font-bold text-white">Utilizatori</h3>
                  </div>
                )}
                {results.users.map(user => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-4 hover:bg-gray-800/30 transition-colors border-b border-gray-800 cursor-pointer"
                    onClick={() => onViewProfile && onViewProfile(user.id)}
                    data-testid={`search-user-${user.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex-shrink-0 overflow-hidden">
                        {user.avatar_url ? (
                          <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white font-bold text-lg">
                            {(user.first_name?.[0] || user.nickname?.[0] || 'U').toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-white">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-gray-500">@{user.nickname}</p>
                        {user.bio && (
                          <p className="text-gray-400 text-sm mt-1 line-clamp-1">{user.bio}</p>
                        )}
                        <p className="text-gray-600 text-xs mt-1">{user.followers_count} followers</p>
                      </div>
                    </div>
                    
                    {user.id !== currentUserId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFollowToggle(user.id, user.is_following);
                        }}
                        className={`px-4 py-1.5 rounded-full font-semibold text-sm transition-colors ${
                          user.is_following
                            ? 'bg-transparent border border-gray-600 text-white hover:border-red-500 hover:text-red-500'
                            : 'bg-white text-black hover:bg-gray-200'
                        }`}
                        data-testid={`follow-user-${user.id}`}
                      >
                        {user.is_following ? 'Following' : 'Follow'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Posts Results */}
            {(activeTab === 'all' || activeTab === 'posts') && results.posts.length > 0 && (
              <div>
                {activeTab === 'all' && results.users.length > 0 && (
                  <div className="px-4 py-3 border-b border-gray-800 mt-2">
                    <h3 className="text-lg font-bold text-white">Postări</h3>
                  </div>
                )}
                {results.posts.map(post => (
                  <div 
                    key={post.id}
                    className="border-b border-gray-800 p-4 hover:bg-gray-800/30 transition-colors"
                    data-testid={`search-post-${post.id}`}
                  >
                    {/* Post Header */}
                    <div className="flex items-start space-x-3">
                      <button
                        onClick={() => onViewProfile && onViewProfile(post.user_id)}
                        className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 overflow-hidden hover:ring-2 hover:ring-cyan-500 transition-all"
                      >
                        {post.user_avatar ? (
                          <img src={post.user_avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white font-bold">
                            {(post.user_name?.[0] || 'U').toUpperCase()}
                          </div>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onViewProfile && onViewProfile(post.user_id)}
                            className="font-bold text-white hover:underline truncate"
                          >
                            {post.user_name}
                          </button>
                          <span className="text-gray-500 truncate">@{post.user_nickname}</span>
                          <span className="text-gray-600">·</span>
                          <span className="text-gray-500 text-sm">{formatDate(post.created_at)}</span>
                        </div>

                        {/* Highlight search term in content */}
                        <p className="text-white mt-1 whitespace-pre-wrap">
                          {highlightSearchTerm(post.content, searchQuery)}
                        </p>

                        {/* Images */}
                        {post.images && post.images.length > 0 && (
                          <div className="mt-3 rounded-2xl overflow-hidden">
                            <img
                              src={`${API_URL}${post.images[0].url || post.images[0].thumbnail_url}`}
                              alt="Post image"
                              className="w-full max-h-64 object-cover"
                            />
                          </div>
                        )}

                        {/* Post Interactions */}
                        <div className="mt-3">
                          <PostInteractions
                            postId={post.id}
                            interactions={postsInteractions[post.id] || {
                              like_count: post.like_count,
                              repost_count: post.repost_count,
                              comments_count: post.comments_count
                            }}
                            onLike={onLike}
                            onRepost={onRepost}
                            onQuote={onQuote}
                            onComment={onComment}
                            onShare={onShare}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Helper function to highlight search term
const highlightSearchTerm = (text, searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, i) => 
    regex.test(part) ? (
      <span key={i} className="bg-cyan-500/30 text-cyan-300 rounded px-0.5">{part}</span>
    ) : (
      part
    )
  );
};

export default SearchPage;
